import { useState, useEffect } from 'react';
import { Howl, Howler } from 'howler';

import style from "../../../../../../theme/SuperCerebrosDice.module.css";
import estiloJuegos from "../../../../../../theme/Juegos.module.css";
import "../../../../../../theme/pages.css";

import { Link, useNavigate } from 'react-router-dom';
import PopoverSuperCerebrosDice from './componentes/PopoverSuperCerebrosDice';
import { test_general } from '../../../../../../datos/DatosTests';
import { nomProgSuperCerebrosDice } from '../../../../../../datos/nombresBD';
import { useDato } from '../../../../../../modelos/auth';
import { newActivity, changePrograma, changeCondicion } from '../../../../../../redux/actividad/reducerActividad';
import { initialNeuronas } from '../../../../../../redux/auth/reducerAuth';
import { terminadoJuego, changePuntaje } from '../../../../../../redux/juegoSimultaneo/reducerJuegoSimultaneo';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { useJuegos } from '../../../../../../repositorio/controlador/useJuegos';

/* ============================================================
   === PUNTAJE (EDITABLE) =====================================
   Curva convexa + mezcla lineal para crecimiento gradual.
   - MAX  : neuronas tope
   - K_MAX: racha donde prácticamente saturas
   - GAMMA: convexidad (>1 = crece lento al inicio, fuerte al final)
   - BLEND: mezcla lineal para no “aplastar” demasiado el inicio
   ============================================================ */
const CURVA = "500@15"; // Cambia a "1000@20" si deseas el otro preset

const CURVAS: Record<string, { MAX: number; K_MAX: number; GAMMA: number; BLEND?: number }> = {
  "500@15": { MAX: 500, K_MAX: 15, GAMMA: 2.2, BLEND: 0.10 },
  "1000@20": { MAX: 1000, K_MAX: 20, GAMMA: 2.0, BLEND: 0.10 },
};


const USE_TIMER = false;       // <- pon true si quieres reactivar el tiempo
const TIME_LIMIT = 20;         // segundos (si USE_TIMER=true)

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

function calcularNeuronasPorRacha(racha: number): number {
  const cfg = CURVAS[CURVA] ?? CURVAS["1000@20"];
  const MAX = cfg.MAX;
  const K_MAX = Math.max(1, cfg.K_MAX);
  const GAMMA = Math.max(1.0001, cfg.GAMMA);
  const BLEND = clamp(cfg.BLEND ?? 0.0, 0, 1);

  const r = clamp(racha / K_MAX, 0, 1);
  const conv = Math.pow(r, GAMMA);
  const mezcla = BLEND * r + (1 - BLEND) * conv;

  return Math.round(MAX * mezcla);
}

/* ============================================================
   === AUDIO: crear una sola vez y limpiar al desmontar =======
   ============================================================ */
type Colores = 'rojo' | 'azul' | 'amarillo' | 'rosado' | 'morado' | 'verde';

function useHowls() {
  const [howls, setHowls] = useState<Record<Colores, Howl> | null>(null);

  useEffect(() => {
    const mapa: Record<Colores, Howl> = {
      rojo: new Howl({ src: ['/assets/sounds/sound_do.mp3'], preload: true, html5: false, volume: 0.6, pool: 1 }),
      azul: new Howl({ src: ['/assets/sounds/sound_re.mp3'], preload: true, html5: false, volume: 0.6, pool: 1 }),
      amarillo: new Howl({ src: ['/assets/sounds/sound_mi.mp3'], preload: true, html5: false, volume: 0.6, pool: 1 }),
      rosado: new Howl({ src: ['/assets/sounds/sound_fa.mp3'], preload: true, html5: false, volume: 0.6, pool: 1 }),
      morado: new Howl({ src: ['/assets/sounds/sound_sol.mp3'], preload: true, html5: false, volume: 0.6, pool: 1 }),
      verde: new Howl({ src: ['/assets/sounds/sound_la.mp3'], preload: true, html5: false, volume: 0.6, pool: 1 }),
    };

    setHowls(mapa);

    return () => {
      // Limpieza: detener y descargar buffers para liberar el AudioContext
      Object.values(mapa).forEach(h => {
        try { h.stop(); h.unload(); } catch { /* noop */ }
      });
      // (opcional) suspender contexto si ya no habrá audio
      // try { Howler.ctx?.suspend(); } catch {}
    };
  }, []);

  return howls;
}

const JuegoSuperCerebrosDice = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.userId);
  const testId = useAppSelector((state) => state.activity.id);
  const esJuego = useAppSelector((state) => state.activity.id === test_general);
  const neuronas = useAppSelector((state) => state.auth.neuronas);
  const esEntrenamiento = useAppSelector((state) => state.activity.id == "entrenamiento");
  const idEntrenamiento = useAppSelector((state) => state.entrenamiento.current_entrenamiento?.id);

  const { state } = useDato();
  const {
    guardarDatosCalificacion,
    guardarDatosCalificacionTestGeneral,
    getCalificacionGeneral,
  } = useJuegos();

  const segundaEtapa = 2;

  const [etapa, setEtapa] = useState<number>(1);
  const [secuencia, setSecuencia] = useState<string[]>([]);
  const [secuenciaUsuario, setSecuenciaUsuario] = useState<string[]>([]);
  const [turno, setTurno] = useState<number>(0);
  const [juegoTerminado, setJuegoTerminado] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const [botonResaltado, setBotonResaltado] = useState<string>('');
  const [cantidadCorrecta, setCantidadCorrecta] = useState<number>(0); // mejor racha de esta partida
  const [ultimoPuntaje, setUltimoPuntaje] = useState<number>(0);       // record personal
  const [contador, setContador] = useState(10);
  const [nuevasNeuronas, setNuevasNeuronas] = useState<number>(0);
  const [iniciado, setIniciado] = useState(false);
  const [turnoJugador, setTurnoJugador] = useState(false);
  const [mensaje, setMensaje] = useState<string>("");

  const colores: Colores[] = ['rojo', 'verde', 'azul', 'amarillo', 'rosado', 'morado'];
  const howls = useHowls();

  /* ===================== AUDIO HELPERS ===================== */
  const reproducirSonido = (tecla: Colores) => {
    return new Promise<void>((resolve) => {
      if (!howls) return resolve();
      const s = howls[tecla];
      if (!s) return resolve();

      let done = false;
      const safeResolve = () => { if (!done) { done = true; resolve(); } };

      try {
        // Asegura contexto activo (algunas plataformas lo suspenden)
        if (Howler.ctx?.state === 'suspended') {
          Howler.ctx.resume().catch(() => { });
        }

        // Evita stacking/clipping: corta la nota previa de esa tecla
        s.stop();
        s.seek(0);
        s.play();

        s.once('end', safeResolve);
        // Safety timeout por si 'end' no llega
        setTimeout(safeResolve, 1200);
      } catch {
        safeResolve();
      }
    });
  };

  const resaltarColor = async (color: Colores) => {
    if (!howls) return;
    setBotonResaltado(color);
    await reproducirSonido(color);
    setBotonResaltado('');
    await sleep(120);
  };

  const mostrarSecuencia = async (sec: string[]) => {
    setTurnoJugador(false);
    setIsVisible(false);
    setMensaje("SuperCerebros Dice");

    for (const c of sec) {
      await resaltarColor(c as Colores);
    }

    // Termina la demo; preparamos el turno del jugador
    await sleep(300);
    setMensaje("Tu Turno");
    setIsVisible(true);

    // 🔑 Ahora sí: resetea el contador y habilita el turno del jugador
    reiniciarContador();   // setContador(10), setJuegoTerminado(false)
    await sleep(1000);
    setTurnoJugador(true);
  };

  /* ===================== LÓGICA DE JUEGO ===================== */
  const GenerarSecuencia = () => {
    setMensaje("SuperCerebros Dice");
    const nuevaSecuencia: string[] = [];
    const nuevoColor = colores[Math.floor(Math.random() * colores.length)];
    nuevaSecuencia.push(nuevoColor);
    setSecuencia(nuevaSecuencia);
    setCantidadCorrecta(0);
    setTurno(0);
    setSecuenciaUsuario([]);
    setJuegoTerminado(false);
    setIsVisible(true);
    setTurnoJugador(false);
    setTimeout(() => {
      setIsVisible(false);
      mostrarSecuencia(nuevaSecuencia);
    }, 1000);
  };

  const aumentarSecuencia = () => {
    const nuevaSecuencia = [...secuencia];
    const nuevoColor = colores[Math.floor(Math.random() * colores.length)];
    nuevaSecuencia.push(nuevoColor);
    setSecuencia(nuevaSecuencia);
    return nuevaSecuencia;
  };

  const manejarClicColor = (color: string) => {
    reiniciarContador();
    const nuevaSecuenciaUsuario = [...secuenciaUsuario, color];
    setSecuenciaUsuario(nuevaSecuenciaUsuario);

    if (color.length !== 0) {
      // No esperamos aquí: el highlight/sonido del clic puede ser inmediato
      resaltarColor(color as Colores);
      if (nuevaSecuenciaUsuario.length === secuencia.length) {
        verificarSecuencia(nuevaSecuenciaUsuario);
      } else {
        reiniciarContador();
      }
    } else {
      verificarSecuencia(nuevaSecuenciaUsuario);
    }
  };

  const verificarSecuencia = (usuarioSecuencia: string[]) => {
    const cantidadCorrectaTemporal = usuarioSecuencia.length;

    if (JSON.stringify(secuencia) === JSON.stringify(usuarioSecuencia)) {
      // Acierta: aumenta secuencia
      setTurno(turno + 1);
      setSecuenciaUsuario([]);
      setTurnoJugador(false);
      setMensaje("SuperCerebros Dice");
      setIsVisible(true);
      const nuevaSecuencia = aumentarSecuencia();
      setTimeout(() => {
        setIsVisible(false);
        mostrarSecuencia(nuevaSecuencia);
      }, 1000);
      setCantidadCorrecta(cantidadCorrectaTemporal);
    } else {
      // Falla: termina juego
      setJuegoTerminado(true);
      aTerceraPantalla();

      const mejorRacha = Math.max(0, cantidadCorrectaTemporal - 1);
      setCantidadCorrecta(mejorRacha);
      if (mejorRacha > ultimoPuntaje) setUltimoPuntaje(mejorRacha);

      const neuronasNuevas = calcularNeuronasPorRacha(mejorRacha);
      setNuevasNeuronas(neuronasNuevas);

      const neuronasTotales = neuronas + neuronasNuevas;
      dispatch(initialNeuronas({ cantidad: neuronasTotales }));

      if (testId === test_general) {
        guardarDatosCalificacionTestGeneral(
          userId,
          nomProgSuperCerebrosDice,
          neuronasNuevas,
          mejorRacha,
          0, 0,
          false,
          state.nombres,
          state.pais,
          0,
          mejorRacha
        );
      } else {
        guardarDatosCalificacion(
          userId,
          testId,
          nomProgSuperCerebrosDice,
          neuronasNuevas,
          mejorRacha,
          0, 0,
          false,
          state.nombres,
          state.pais
        );
      }
    }
  };

  /* ===================== UI HELPERS ===================== */
  const getDescripcionJuego = () => (
    <div className={estiloJuegos.descripcionJuego}>
      Observa y repite la secuencia de colores que se te muestra. <br /> Cada ronda se vuelve un poco más desafiante.
    </div>
  );

  const reiniciarContador = () => {
    if (!USE_TIMER) return;     // ← sin tiempo: no hacemos nada
    setContador(TIME_LIMIT);
    setJuegoTerminado(false);
  };

  const IniciarContador = () => setIniciado(true);

  const aTerceraPantalla = () => {
    setSecuencia([]);
    // Mantengo la notificación para flujo "juego en línea"
    dispatch(terminadoJuego({ ultimoResultado: (cantidadCorrecta * 10), tiempo: 0, finalizado: true }));
    setEtapa(3);
  };

  const handlePlay = () => {
    GenerarSecuencia();
    if (USE_TIMER) IniciarContador();
    setEtapa(segundaEtapa);
  };

  const handleRanking = () => navigate('/my/juegos/ranking');

  async function handleBackButtonClick(): Promise<void> {
    if (esEntrenamiento) {
      await dispatch(newActivity({ id: idEntrenamiento, name: "entrenamiento" }));
      await dispatch(changePrograma({ id: idEntrenamiento }));
      dispatch(changeCondicion({ condicion: 10 }));
      navigate(-2);
    } else {
      if (testId === 'juego_linea') {
        dispatch(changePuntaje({ verPuntaje: true }));
      } else {
        navigate(-1);
      }
    }
  }

  const constructor = () => {
    getCalificacionGeneral(userId, nomProgSuperCerebrosDice).then((calificacion) => {
      if (calificacion !== undefined) {
        setUltimoPuntaje(calificacion.total ?? 0);
      } else {
        setUltimoPuntaje(0);
      }
    });
  };

  useEffect(() => {
    // Si no hay tiempo, no corremos el contador
    if (!USE_TIMER) return;

    // Solo contar cuando:
    // - ya se inició
    // - NO está terminado
    // - ES turno del jugador
    if (!iniciado || juegoTerminado || !turnoJugador) return;

    const interval = setInterval(() => {
      setContador((prev) => {
        if (prev > 1) return prev - 1;

        // Llegó a 0 DURANTE el turno del jugador ⇒ termina por tiempo
        setJuegoTerminado(true);
        manejarClicColor("");   // fuerza verificación fallida por timeout
        clearInterval(interval);
        return 0;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [iniciado, juegoTerminado, turnoJugador]);

  useEffect(() => {
    constructor();
  }, []);

  return (
    <div id='page' style={(testId === 'juego_linea' ? { height: "100%" } : {})}>

      {(testId !== 'juego_linea' || etapa === 1) &&
        <div className={etapa === 1 ? estiloJuegos.toolbarSCDice : estiloJuegos.toolbar_default}>
          {esJuego && <Link className={estiloJuegos.backJuegos} to=".." relative='path'></Link>}
          <h1 className={`title ${estiloJuegos.tituloJuegos}`} style={etapa !== 1 ? { fontSize: '2.37svh' } : {}}>
            SuperCerebros DICE
          </h1>
        </div>
      }

      {etapa === 1 &&
        <div className={estiloJuegos.contenedorIconoSC}>
          <img src='/assets/componentes/Juegos/SC.svg' className={estiloJuegos.icono}></img>
        </div>
      }

      <div id='content_default' className={style.fondo}>
        {etapa === 1 &&
          <div className={estiloJuegos.primeraPantalla}>
            {getDescripcionJuego()}
            <button className={estiloJuegos.buttonEmpezar} onClick={handlePlay}>
              EMPEZAR
            </button>
            {/* {esJuego &&
              <button id='button_bordes_default' onClick={handleRanking}>
                RANKING
              </button>
            } */}
          </div>
        }

        {etapa === 3 &&
          <div className={style.popover}>
            <PopoverSuperCerebrosDice
              nuevasNeuronas={nuevasNeuronas}
              mensajeInfo={`Obtuviste ${cantidadCorrecta} secuencias correctas`}
              nivel={cantidadCorrecta}
              funcionReiniciar={handlePlay}
              funcionVolver={handleBackButtonClick}
              esJuego={esJuego}
            />
          </div>
        }

        {etapa !== 1 &&
          <div className={style.supercerebrosDice}>
            {isVisible && <div className={style.mensajeTurno}>{mensaje}</div>}
            <div className={style.cortina} style={turnoJugador ? { display: 'none' } : {}}></div>
            <div className={style.tablero}>
              <div className={style.fila}>
                <div className={[style.color, style.rojo, botonResaltado === 'rojo' ? style.resaltado : ''].join(" ")}
                  onClick={() => manejarClicColor('rojo')} />
                <div className={[style.color, style.azul, botonResaltado === 'azul' ? style.resaltado : ''].join(" ")}
                  onClick={() => manejarClicColor('azul')} />
              </div>
              <div className={style.fila}>
                <div className={[style.color, style.amarillo, botonResaltado === 'amarillo' ? style.resaltado : ''].join(" ")}
                  onClick={() => manejarClicColor('amarillo')} />
                <div className={style.color + " " + style.rosado + " " + (botonResaltado === 'rosado' ? style.resaltado : '')}
                  onClick={() => manejarClicColor('rosado')} />
              </div>
              <div className={style.fila}>
                <div className={[style.color, style.morado, botonResaltado === 'morado' ? style.resaltado : ''].join(" ")}
                  onClick={() => manejarClicColor('morado')} />
                <div className={[style.color, style.verde, botonResaltado === 'verde' ? style.resaltado : ''].join(" ")}
                  onClick={() => manejarClicColor('verde')} />
              </div>
            </div>
            <p className={style.nivel}>
              <span>RECORD: {ultimoPuntaje}</span> <span>Actual: {cantidadCorrecta}</span>
            </p>
          </div>
        }
      </div>
    </div>
  );
};

export default JuegoSuperCerebrosDice;
